import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import CustomerSection from './CustomerSection';
import JobBasicsSection from './JobBasicsSection';
import WorkScopeSection from './WorkScopeSection';
import NotesSection from './NotesSection';
import JobFormValidation, { validateJobForm } from './JobFormValidation';
import { useJobs } from '../../hooks/useJobs';
import { dateFromInput, dateToInput, isoDateString } from '../../utils/date-utils';
import { generateWorkdays } from '../../utils/work-day-calculator';

// Map old crew IDs to new crew IDs
function migrateOldCrewId(oldCrewId) {
  if (!oldCrewId) return '';
  
  const crewMigrationMap = {
    'crew-1': 'adam-steve',  // Old "Crew 1 (Adam & Steve)" → new "Adam + Steve"
    'crew-2': '',            // Old "Crew 2 (AJ & Amy)" → no equivalent, clear it
  };
  
  return crewMigrationMap[oldCrewId] !== undefined 
    ? crewMigrationMap[oldCrewId] 
    : oldCrewId; // If not in map, keep as-is (might be valid new ID)
}

export default function NewJobSheet({ isOpen, onClose, presetDate = null, editingJob = null }) {
  const { addJob, updateJob } = useJobs();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const [formData, setFormData] = useState({
    // Customer
    customerId: '',
    customerName: '',
    phoneNumber: '',
    customerEmail: '',
    address: '',
    
    // Job Basics
    name: '', // location/town
    contractNumber: '',
    soldDate: dateToInput(new Date()),
    scheduledDate: '', // Don't auto-fill scheduled date for new jobs
    jobType: 'install',
    status: 'sold',
    
    // Work Scope
    windowCount: 0,
    doorCount: 0,
    durationDays: 1,
    assignedCrew: '',
    isRepair: false,
    isDeliveryOnly: false,
    isPickupAtShop: false,
    
    // Notes
    notes: '',
    internalNotes: ''
  });

  // Load existing job data when editing
  useEffect(() => {
    if (editingJob) {
      setFormData({
        customerId: editingJob.customerId || '',
        customerName: editingJob.customerName || '',
        phoneNumber: editingJob.phoneNumber || '',
        customerEmail: editingJob.customerEmail || '',
        address: editingJob.address || '',
        name: editingJob.name || '',
        contractNumber: editingJob.contractNumber || '',
        soldDate: editingJob.soldDate ? dateToInput(editingJob.soldDate) : dateToInput(new Date()),
        scheduledDate: editingJob.scheduledDate ? dateToInput(editingJob.scheduledDate) : '',
        jobType: editingJob.jobType || 'install',
        status: editingJob.status || 'sold',
        assignedCrew: migrateOldCrewId(editingJob.assignedCrew),
        windowCount: editingJob.windowCount || 0,
        doorCount: editingJob.doorCount || 0,
        isRepair: editingJob.isRepair || false,
        isDeliveryOnly: editingJob.isDeliveryOnly || false,
        isPickupAtShop: editingJob.isPickupAtShop || false,
        durationDays: editingJob.durationDays || 1,
        notes: editingJob.notes || '',
        internalNotes: editingJob.internalNotes || ''
      });
    } else if (!isOpen) {
      // Reset form when closed and not editing
      setFormData({
        customerId: '',
        customerName: '',
        phoneNumber: '',
        customerEmail: '',
        address: '',
        name: '',
        contractNumber: '',
        soldDate: dateToInput(new Date()),
        scheduledDate: '', // Don't auto-fill scheduled date
        jobType: 'install',
        status: 'sold',
        windowCount: 0,
        doorCount: 0,
        durationDays: 1,
        assignedCrew: '',
        isRepair: false,
        isDeliveryOnly: false,
        isPickupAtShop: false,
        notes: '',
        internalNotes: ''
      });
    } else if (presetDate) {
      // Opened from a specific calendar day — schedule it there
      setFormData(prev => ({ ...prev, scheduledDate: dateToInput(presetDate) }));
    }
  }, [editingJob, isOpen, presetDate]);

  function handleChange(updates) {
    setFormData(prev => ({ ...prev, ...updates }));
    // Clear errors when user makes changes
    if (errors.length > 0) {
      setErrors([]);
    }
  }

  async function handleSubmit() {
    // Validate
    const validationErrors = validateJobForm(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);

    try {
      if (editingJob) {
        // Update existing job
        const updates = {
          customerId: formData.customerId || null,
          customerName: formData.customerName.trim(),
          name: formData.name.trim(),
          contractNumber: formData.contractNumber.trim(),
          soldDate: dateFromInput(formData.soldDate),
          scheduledDate: formData.scheduledDate ? dateFromInput(formData.scheduledDate) : null,
          jobType: formData.jobType,
          status: formData.status,
          windowCount: formData.windowCount,
          doorCount: formData.doorCount,
          durationDays: formData.durationDays,
          assignedCrew: formData.assignedCrew || null,
          isRepair: formData.isRepair,
          isDeliveryOnly: formData.isDeliveryOnly,
          isPickupAtShop: formData.isPickupAtShop,
          phoneNumber: formData.phoneNumber.trim(),
          customerEmail: formData.customerEmail.trim(),
          address: formData.address.trim(),
          notes: formData.notes.trim(),
          internalNotes: formData.internalNotes.trim()
        };

        // Regenerate workDays if job is scheduled
        if (formData.scheduledDate && formData.durationDays) {
          const workDays = generateWorkdays(
            dateFromInput(formData.scheduledDate), 
            formData.durationDays
          ).map(date => ({
            id: isoDateString(date),
            date: date,
            status: 'planned',
            note: null,
            createdAt: new Date()
          }));
          
          updates.workDays = workDays;
          console.log('🔄 Regenerated workDays:', workDays.map(wd => wd.id));
        } else if (!formData.scheduledDate) {
          // If unscheduling a job, clear workDays
          updates.workDays = [];
        }

        await updateJob(editingJob.id, updates);
        console.log('✅ Job updated successfully');
      } else {
        // Create new job
        const jobData = {
          customerId: formData.customerId || null,
          customerName: formData.customerName.trim(),
          name: formData.name.trim(),
          contractNumber: formData.contractNumber.trim(),
          soldDate: dateFromInput(formData.soldDate),
          scheduledDate: formData.scheduledDate ? dateFromInput(formData.scheduledDate) : null,
          jobType: formData.jobType,
          status: formData.status,
          windowCount: formData.windowCount,
          doorCount: formData.doorCount,
          durationDays: formData.durationDays,
          assignedCrew: formData.assignedCrew || null,
          isRepair: formData.isRepair,
          isDeliveryOnly: formData.isDeliveryOnly,
          isPickupAtShop: formData.isPickupAtShop,
          phoneNumber: formData.phoneNumber.trim(),
          customerEmail: formData.customerEmail.trim(),
          address: formData.address.trim(),
          notes: formData.notes.trim(),
          internalNotes: formData.internalNotes.trim(),
          statusHistory: [{
            status: formData.status,
            timestamp: new Date(),
            note: 'Job created'
          }],
          images: [],
          contracts: [],
          workDays: []
        };

        await addJob(jobData);
        console.log('✅ Job created successfully');
      }
      
      onClose();
    } catch (error) {
      console.error('❌ Error saving job:', error);
      setErrors([editingJob ? 'Failed to update job. Please try again.' : 'Failed to create job. Please try again.']);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingJob ? "Edit Job" : "New Job"}
      size="large"
    >
      <div style={{
        maxHeight: '70vh',
        overflowY: 'auto',
        padding: '24px'
      }}>
        {/* Validation Errors */}
        <JobFormValidation errors={errors} />

        {/* Customer Section */}
        <CustomerSection
          formData={formData}
          onChange={handleChange}
        />

        <div style={{
          height: '1px',
          background: 'var(--cell-border)',
          margin: '24px 0'
        }} />

        {/* Job Basics Section */}
        <JobBasicsSection
          formData={formData}
          onChange={handleChange}
        />

        <div style={{
          height: '1px',
          background: 'var(--cell-border)',
          margin: '24px 0'
        }} />

        {/* Work Scope Section */}
        <WorkScopeSection
          formData={formData}
          onChange={handleChange}
        />

        <div style={{
          height: '1px',
          background: 'var(--cell-border)',
          margin: '24px 0'
        }} />

        {/* Notes Section */}
        <NotesSection
          formData={formData}
          onChange={handleChange}
        />
      </div>

      {/* Footer Actions */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid var(--cell-border)',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        background: 'var(--bg-secondary)'
      }}>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? (editingJob ? 'Updating...' : 'Creating...') : (editingJob ? 'Update Job' : 'Create Job')}
        </Button>
      </div>
    </Modal>
  );
}